import React, { useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import boardSettingsService from "../../../services/api/boardSettingsService";
import { updateBoard } from "../../../redux/actions";

const formControlSx = { width: "100%" };
const compactFieldProps = { fullWidth: true, size: "small", variant: "outlined" };
const autocompleteSx = {
  width: "100%",
  "& .MuiAutocomplete-inputRoot": {
    minHeight: 40,
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "9px !important",
    alignItems: "center",
  },
};

const SUBTASK_CARD_TYPE = "Subtask";
// subtasks move between fixed states rather than lanes
const SUBTASK_STATE_OPTIONS = [
  { id: 1, name: "To Do" },
  { id: 2, name: "Doing" },
  { id: 3, name: "Done" },
];

const isSubtaskRule = (cardTypes) => (cardTypes || [])
  .some((cardType) => cardType.toLowerCase() === SUBTASK_CARD_TYPE.toLowerCase());

// lanes come from the board settings selector, falling back to the raw board payload
const normalizeLanes = (laneList) => (laneList || [])
  .filter((lane) => lane && (lane.id || lane.id === 0))
  .map((lane) => ({ id: Number.parseInt(lane.id), name: lane.fullname || lane.name || String(lane.id) }));

const newRule = () => ({
  id: `flow-${Date.now()}`,
  name: "",
  card_types: [],
  from_lane_id: "",
  allowed_lane_ids: [],
  from_state: "",
  allowed_states: [],
  enabled: true,
  order: 0,
});

function CardFlowRules({ board, lanes, allLanes, updateBoardSettings }) {
  const [rules, setRules] = useState([]);
  const [draft, setDraft] = useState(newRule);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const laneOptions = normalizeLanes(lanes?.length ? lanes : board.lanes);
  // naming resolves against every lane, so rules still read correctly when a lane gains children
  const laneNameOptions = normalizeLanes(allLanes?.length ? allLanes : (lanes?.length ? lanes : board.lanes));
  const cardTypeOptions = (board.settings?.card_type || []).map((cardType) => cardType.name);
  const draftTargetsSubtasks = isSubtaskRule(draft.card_types);

  useEffect(() => {
    boardSettingsService.getCardFlow(board.id).then(
      (response) => setRules(response.data.rules || []),
      () => setError("Unable to load workflow patterns.")
    );
  }, [board.id]);

  const laneName = (laneId) => laneNameOptions
    .find((lane) => lane.id === Number.parseInt(laneId))?.name || `Lane ${laneId}`;

  const stateName = (stateId) => SUBTASK_STATE_OPTIONS
    .find((state) => state.id === Number.parseInt(stateId))?.name || `State ${stateId}`;

  const ruleSummary = (rule) => (isSubtaskRule(rule.card_types)
    ? `${stateName(rule.from_state)} → ${(rule.allowed_states || []).map(stateName).join(", ")}`
    : `${laneName(rule.from_lane_id)} → ${(rule.allowed_lane_ids || []).map(laneName).join(", ")}`);

  const updateDraft = (field, value) => setDraft({ ...draft, [field]: value });

  // switching a draft between lane and state patterns clears the selections that no longer apply
  const changeCardTypes = (value) => setDraft({
    ...draft,
    card_types: value,
    ...(isSubtaskRule(value)
      ? { from_lane_id: "", allowed_lane_ids: [] }
      : { from_state: "", allowed_states: [] }),
  });

  const resetDraft = () => {
    setDraft(newRule());
    setEditing(false);
    setError("");
  };

  const editRule = (rule) => {
    setDraft({
      ...newRule(),
      ...rule,
      // a lane that gained children is no longer selectable, so clear it and make the user re-pick
      from_lane_id: laneOptions.some((lane) => lane.id === Number.parseInt(rule.from_lane_id))
        ? Number.parseInt(rule.from_lane_id) : "",
      allowed_lane_ids: (rule.allowed_lane_ids || [])
        .map((laneId) => Number.parseInt(laneId))
        .filter((laneId) => laneOptions.some((lane) => lane.id === laneId)),
      from_state: rule.from_state ? Number.parseInt(rule.from_state) : "",
      allowed_states: (rule.allowed_states || []).map((state) => Number.parseInt(state)),
      card_types: rule.card_types || [],
    });
    setEditing(true);
    setError("");
  };

  const changeSourceLane = (laneId) => setDraft({
    ...draft,
    from_lane_id: laneId,
    allowed_lane_ids: draft.allowed_lane_ids.filter((id) => id !== laneId),
  });

  const changeSourceState = (stateId) => setDraft({
    ...draft,
    from_state: stateId,
    allowed_states: draft.allowed_states.filter((id) => id !== stateId),
  });

  const persist = (nextRules, onDone) => {
    boardSettingsService.updateCardFlow({ boardId: board.id, rules: nextRules }).then(
      (response) => {
        const savedRules = response.data.rules || nextRules;
        setRules(savedRules);
        updateBoardSettings(board.id, { settings: { ...board.settings, card_flow: { version: 1, rules: savedRules } } });
        setSaved(true);
        if (onDone) onDone();
      },
      (requestError) => setError(requestError.response?.data?.message || "Unable to save workflow pattern.")
    );
  };

  const saveRule = () => {
    if (!draft.name.trim()) {
      setError("Pattern name is required.");
      return;
    }
    if (!draft.card_types.length) {
      setError("Select at least one card type this pattern applies to.");
      return;
    }
    if (draftTargetsSubtasks && draft.card_types.length > 1) {
      setError("Subtask patterns cannot be combined with other card types.");
      return;
    }
    if (draftTargetsSubtasks) {
      if (!draft.from_state) {
        setError("Select the state this pattern applies to.");
        return;
      }
      if (!draft.allowed_states.length) {
        setError("Select at least one state the subtask is allowed to move to.");
        return;
      }
    } else {
      if (!draft.from_lane_id
        || !laneOptions.some((lane) => lane.id === Number.parseInt(draft.from_lane_id))) {
        setError("Select the lane this pattern applies to.");
        return;
      }
      if (draft.allowed_lane_ids
        .some((laneId) => !laneOptions.some((lane) => lane.id === Number.parseInt(laneId)))) {
        setError("One or more target lanes no longer exist. Select them again.");
        return;
      }
      if (!draft.allowed_lane_ids.length) {
        setError("Select at least one lane the card is allowed to move to.");
        return;
      }
    }
    const otherRules = rules.filter((rule) => rule.id !== draft.id);
    persist([...otherRules, { ...draft, order: otherRules.length }], resetDraft);
  };

  const toggleRule = (rule) => persist(rules
    .map((item) => (item.id === rule.id ? { ...item, enabled: !item.enabled } : item)));

  const deleteRule = () => {
    boardSettingsService.deleteCardFlow(board.id, deleteTarget.id).then(
      () => {
        const remainingRules = rules.filter((rule) => rule.id !== deleteTarget.id);
        setRules(remainingRules);
        updateBoardSettings(board.id, { settings: { ...board.settings, card_flow: { version: 1, rules: remainingRules } } });
        setDeleteTarget(null);
        setSaved(true);
      },
      (requestError) => setError(requestError.response?.data?.message || "Unable to delete workflow pattern.")
    );
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 900, pb: 4 }}>
      <Typography className="cardHeaderTitle" variant="h6">Card Workflow Patterns</Typography>
      <Typography color="text.secondary" variant="body2">
        Restrict where a card can travel from a lane. Every pattern must name the card types it applies to,
        and rules for the same lane and card type are combined. Choose the <b>Subtask</b> card type to restrict
        subtask state changes instead of lane moves.
      </Typography>
      {saved && <Alert severity="success" onClose={() => setSaved(false)}>Workflow pattern saved</Alert>}
      {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
      <Stack spacing={1}>
        <Typography className="cardHeaderTitle" variant="subtitle1">Saved patterns</Typography>
        {rules.length === 0 && <Typography color="text.secondary">No workflow patterns have been created.</Typography>}
        {rules.map((rule) => (
          <Stack key={rule.id} direction="row" spacing={1} alignItems="center" sx={{ borderBottom: 1, borderColor: "divider", py: 1 }}>
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {rule.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {(rule.card_types?.length ? rule.card_types.join(", ") : "All card types")}
                {` : ${ruleSummary(rule)}`}
              </Typography>
            </Stack>
            <Tooltip title="Edit pattern"><IconButton size="small" color="primary" onClick={() => editRule(rule)}><EditIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title={rule.enabled ? "Disable pattern" : "Enable pattern"}>
              <FormControlLabel
                control={<Switch checked={rule.enabled} onChange={() => toggleRule(rule)} />}
                label=""
                sx={{ m: 0 }}
              />
            </Tooltip>
            <Tooltip title="Delete pattern"><IconButton size="small" color="error" onClick={() => setDeleteTarget(rule)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography className="cardHeaderTitle" variant="subtitle1">{editing ? "Edit pattern" : "New pattern"}</Typography>
        {editing && <Button size="small" variant="outlined" onClick={resetDraft}>Cancel edit</Button>}
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField {...compactFieldProps} label="Pattern name" value={draft.name} onChange={(event) => updateDraft("name", event.target.value)} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            multiple
            options={cardTypeOptions}
            value={draft.card_types}
            onChange={(_event, value) => changeCardTypes(value)}
            renderInput={(params) => (
              <TextField {...params} {...compactFieldProps} required label="Card types" placeholder="Select card types" />
            )}
            sx={autocompleteSx}
          />
        </Grid>
        {draftTargetsSubtasks ? (
          <>
            <Grid item xs={12} md={6}>
              <FormControl size="small" sx={formControlSx}>
                <InputLabel id="card-flow-from-state">From state</InputLabel>
                <Select
                  labelId="card-flow-from-state"
                  label="From state"
                  value={draft.from_state}
                  onChange={(event) => changeSourceState(event.target.value)}
                >
                  {SUBTASK_STATE_OPTIONS.map((state) => <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={SUBTASK_STATE_OPTIONS.filter((state) => state.id !== Number.parseInt(draft.from_state))}
                getOptionLabel={(option) => option.name}
                value={SUBTASK_STATE_OPTIONS.filter((state) => draft.allowed_states.includes(state.id))}
                onChange={(_event, value) => updateDraft("allowed_states", value.map((state) => state.id))}
                renderInput={(params) => (
                  <TextField {...params} {...compactFieldProps} label="Allowed target states" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                      deleteIcon={<CancelIcon />}
                      onDelete={() => updateDraft("allowed_states", draft.allowed_states.filter((stateId) => stateId !== option.id))}
                    />
                  ))
                }
                sx={autocompleteSx}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} md={6}>
              <FormControl size="small" sx={formControlSx}>
                <InputLabel id="card-flow-from-lane">From lane</InputLabel>
                <Select
                  labelId="card-flow-from-lane"
                  label="From lane"
                  value={draft.from_lane_id}
                  onChange={(event) => changeSourceLane(event.target.value)}
                >
                  {laneOptions.map((lane) => <MenuItem key={lane.id} value={lane.id}>{lane.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={laneOptions.filter((lane) => lane.id !== Number.parseInt(draft.from_lane_id))}
                getOptionLabel={(option) => option.name}
                value={laneOptions.filter((lane) => draft.allowed_lane_ids.includes(lane.id))}
                onChange={(_event, value) => updateDraft("allowed_lane_ids", value.map((lane) => lane.id))}
                renderInput={(params) => (
                  <TextField {...params} {...compactFieldProps} label="Allowed target lanes" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                      deleteIcon={<CancelIcon />}
                      onDelete={() => updateDraft("allowed_lane_ids", draft.allowed_lane_ids.filter((laneId) => laneId !== option.id))}
                    />
                  ))
                }
                sx={autocompleteSx}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" variant="outlined" onClick={resetDraft}>New pattern</Button>
        <Button size="small" variant="contained" onClick={saveRule}>Save pattern</Button>
      </Stack>
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete workflow pattern?</DialogTitle>
        <DialogContent>
          <DialogContentText>This will permanently remove {deleteTarget?.name || "this pattern"}.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" onClick={deleteRule}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  updateBoardSettings: (id, board) => updateBoard(id, board),
}, dispatch);

export default connect(null, mapDispatchToProps)(CardFlowRules);
